// ============================================================
//  VRISHA MUNI — WEBSITE CONTENT
//  Edit ALL website text in this one file. Save and rebuild
//  (yarn build) for changes to go live.
//
//  PHOTOS:
//  - Lookbook photos: just drop files into src/assets/lookbook
//    named lookbook-06.jpg, lookbook-07.jpg, etc. They appear
//    on the site automatically (sorted by filename).
//    Extra photos of the same look: lookbook-06-2.jpg, -3.jpg…
//    Optional captions for new photos: add an entry to
//    "lookbookCaptions" below (same name, without extension).
//  - Optional but recommended for speed: also add a small copy
//    (max ~600px wide) of each new photo to
//    src/assets/lookbook/thumbs with the SAME name. The grid
//    uses it as a fast-loading thumbnail. If skipped, the grid
//    simply uses the full photo instead - nothing breaks.
//  - Other photos (hero, about, spotlight): replace the file in
//    src/assets keeping the same name, or change the import below.
// ============================================================

import hero1 from "./assets/hero-1.webp";
import hero2 from "./assets/hero-2.webp";
import about1 from "./assets/about-1.webp";
import about2 from "./assets/about-2.webp";

import vaani1 from "./assets/spotlight/vaani-kapoor-1.webp";
import vaani2 from "./assets/spotlight/vaani-kapoor-2.webp";
import manushi1 from "./assets/spotlight/manushi-chhillar-1.webp";
import manushi2 from "./assets/spotlight/manushi-chhillar-2.webp";
import mrunal1 from "./assets/spotlight/mrunal-thakur-1.webp";
import mrunal2 from "./assets/spotlight/mrunal-thakur-2.webp";
import chitrangada1 from "./assets/spotlight/chitrangada-singh-1.webp";
import chitrangada2 from "./assets/spotlight/chitrangada-singh-2.webp";
import chitrangada3 from "./assets/spotlight/chitrangada-singh-3.webp";

// ------------------------------------------------------------
// BRAND & CONTACT DETAILS
// ------------------------------------------------------------
const brand = {
  name: "Vrisha Muni",
  monogram: "VM",
  title: "Bridal Styling Atelier",
  tagline: "Personal style, thoughtfully curated to create a wedding wardrobe that feels distinctly yours.",
  email: "styling@vrishamuni.com",
  instagram: "@vrishamuni",
  instagramUrl: "https://instagram.com/vrishamuni",
  phone: "+91 98929 12555",
  locations: "Mumbai · New Delhi · London · New York",
};

// ------------------------------------------------------------
// NAVIGATION MENU (label = menu text, target = section it jumps to)
// ------------------------------------------------------------
const nav = [
  { label: "About", target: "#about" },
  { label: "Services", target: "#services" },
  { label: "Recent Looks", target: "#recent-looks" },
  { label: "Designers", target: "#designers" },
  { label: "Testimonials", target: "#testimonials" },
];

// ------------------------------------------------------------
// HERO (top of the page)
// ------------------------------------------------------------
const hero = {
  eyebrow: "Bridal Styling Atelier - India & Worldwide",
  lines: [
    "Personal style,",
    "thoughtfully curated.",
    "Distinctly yours.",
  ],
  intro: "We work closely with each bride to shape a wardrobe that feels personal, cohesive and effortless - from the designers and jewellery to the smallest finishing detail.",
  image: hero1,          // main portrait
  secondaryImage: hero2, // small inset photo (bottom-left of portrait)
  badge: "Est. with love · Curating since day one",
  scrollHint: "Scroll to begin",
};

// ------------------------------------------------------------
// ABOUT SECTION
// ------------------------------------------------------------
const about = {
  eyebrow: "About",
  heading: "About",
  portrait: about1,
  images: [about1, about2], // gallery shown in the portrait frame (arrows appear when more than one)
  portraitCaption: "Vrisha Muni - stylist, curator, confidante",
  paragraphs: [
    "Styling, for us, is about understanding the person before the wardrobe. Every bride comes with her own sense of style, her own way of carrying clothes, and her own idea of how she wants to feel. Our role is to understand that instinct and build on it - bringing together the right designers, jewellery, accessories and details to create a wardrobe that feels cohesive, considered and entirely her own.",
    "Over time, we've built strong relationships across India's fashion and wedding industry - with designers, couture houses, jewellery brands, boutiques and artisans. These relationships, along with a close understanding of the Indian wedding landscape, allow us to approach each wardrobe with both perspective and access, while keeping the process personal and highly considered.",
    "We work closely with our brides through every stage of the process, from defining the overall direction and discovering the right pieces to fittings, jewellery and the final details. Whether it's the bridal lehenga, the mother's sari or the accessories that bring a look together, nothing is treated as an afterthought.",
    "For us, good styling is about knowing what belongs, what doesn't, and how to bring it all together in a way that feels distinctly yours.",
  ],
  pullQuote: "We believe in finding the right balance between what you have imagined and what you never knew you wanted.",
};

// ------------------------------------------------------------
// SERVICES SECTION (the 5 numbered rows)
// ------------------------------------------------------------
const services = {
  eyebrow: "Services",
  heading: "How we can work together",
  intro: "Every engagement is bespoke. These are the five ways we most often work with brides and their families.",
  items: [
    {
      index: "01",
      title: "Bridal Styling",
      short: "The complete wedding wardrobe, composed around you.",
      description: "Every bride already has an instinct for her own style - our role is to help refine it into something coherent she can actually wear across an entire wedding. That starts with understanding you: your taste, what you're drawn to, what you'll want to wear again long after the wedding is over. From there, we build a complete look for each function. The outfit, the jewellery, and every finishing detail is decided together, not chosen separately and hoped to match. We sit in on fittings, brief your hair and makeup artist ahead of time, and we're there for the final adjustments before you walk out. The goal isn't just cohesion - it's getting you excited about what's actually possible for your own day.",
    },
    {
      index: "02",
      title: "Personal Shopping & Curation",
      short: "Private access to India's finest ateliers.",
      description: "Trousseau shopping is one of the most overwhelming parts of a wedding - hundreds of options and limited time. We step in as your single point of contact: scouting designers and stores ahead of your visit, booking appointments in advance, and arranging pieces to try so no time is lost once you're there. Every recommendation draws on relationships with designers and ateliers, one-of-a-kind pieces and not on what's sitting on every other rack this season. For brides shopping from outside India, the same process runs over calls and video walkthroughs, with pieces sourced and shipped on your behalf. The result: a trousseau built with intention, not assembled under pressure in a single afternoon.",
    },
    {
      index: "03",
      title: "Fine Jewellery Styling & Sourcing",
      short: "Heirlooms chosen with intention.",
      description: "Jewellery has always been a genuine passion of ours. It is not just an accessory, but something a bride carries with her long after the wedding ends. We travel across India, from established ateliers to smaller craftsmen most people never hear of, to find pieces that balance timeless craftsmanship with a contemporary eye. Every piece is chosen with the same care we bring to styling, so it doesn't just look right in a single photograph - it earns its place in your life for years to come. For every bride, jewellery is never just a purchase. It's something worth getting right.",
    },
    {
      index: "04",
      title: "Bridal Party & Family Styling",
      short: "Everyone you love, dressed in the same story.",
      description: "A wedding is never just about one look - it's the mother who wants to feel her best, the sisters and closest friends standing beside you, the family members who've waited years for this day. We style them too, with the same care and attention as the bride herself, so the wedding reads as one considered story rather than a single styled photo surrounded by everyone else. Every generation has its own sense of style, and our work is to honour that without losing the thread that ties the whole wedding together. Because the people closest to you deserve to feel just as good as you do.",
    },
    {
      index: "05",
      title: "Consultancy & Concept Direction",
      short: "Bringing the bigger picture together.",
      description: "A considered approach to the visual direction of your wedding and its most photographed moments. From developing the overall mood and styling direction to planning engagement and pre-wedding shoots, every element is considered in relation to the couple, the setting and the story being created. This can include creative concepts, location and visual references, as well as styling and direction on the day. The aim is to bring the different elements together with a clear point of view, so the final images feel cohesive, personal and beautifully considered.",
    },
  ],
};

// ------------------------------------------------------------
// LOOKBOOK SECTION (heading text only - photos load automatically
// from src/assets/lookbook, sorted by filename)
// ------------------------------------------------------------
const looks = {
  eyebrow: "Recent Looks",
  heading: "The lookbook",
  intro: "A selection of recent brides and celebrations. Tap any look to step inside the detail.",
};

// ------------------------------------------------------------
// LOOKBOOK CAPTIONS (optional)
// One entry per photo in src/assets/lookbook, keyed by filename
// WITHOUT the extension. Photos without an entry still appear -
// they simply show without a caption.
// ------------------------------------------------------------
const lookbookCaptions = {
  "lookbook-01": {
    title: "Tinted in Colour",
    client: "Juhi Mehta",
    location: "Jaipur",
    category: "Haldi",
    details: "Inspired by nature's palette, a custom Mohhar Pahi Saree from Khokee elegantly shifts from purple to orange tones on the pallu, hand-printed and adorned with golden embroidery that flows like leaf veins. Crafted from delicate silk, it offers a breezy lightness for effortless movement, enhancing your curves with subtle grace. Styled with a long string of pink roses as haathphool, to bring out the outfit's detailing.",
  },
  "lookbook-02": {
    title: "Rang Saari",
    client: "Jhernna S Jumaani & Daughter, Vaishnevi",
    location: "Goa",
    category: "Haldi",
    details: "For the haldi, the mother and sister of the groom wanted to twin in a way that felt coordinated yet personal. We chose vibrant rust lehengas with soft pleating and delicate mirror-work borders, creating a playful, festive look that worked beautifully together while still allowing each of them to style the look in her own way.",
  },
  "lookbook-03": {
    title: "Electric Blue",
    client: "Juhi Mehta",
    location: "Jaipur",
    category: "Sangeet",
    details: "For the sangeet, the bride wore a custom Gaurav Gupta lehenga in electric blue, sculpted with his signature fluidity - built to move through a full night of dancing, not just hold a pose for photographs. The colour did the heavy lifting under the lights, structured enough to command the room and easy enough to actually live in till the night wound down.",
  },
  "lookbook-04": {
    title: "A Golden Afternoon",
    client: "Rupaah & Kunal Mehta",
    location: "Jaipur",
    category: "Welcome Lunch",
    details: "For the welcome lunch, the bride's parents were dressed in custom bespoke outfits, styled in colours that spoke to each other rather than matched outright - one carrying the warmth of the vineyard's golden hour, the other grounded in a deeper, earthier tone. Built for a daytime affair among the vines, both pieces were tailored for ease and comfort, while looking effortlessly chic.",
  },
  "lookbook-05": {
    title: "Scarlet Evening",
    client: "Amisha",
    location: "Mumbai",
    category: "Sangeet & Reception",
    details: "For the reception, Amisha wore a Mishru ensemble that felt polished, elegant and quietly glamorous. We wanted the long, sculpted silhouette to have presence without overwhelming her, keeping the proportions clean and easy to move in. The rich pink brought a modern edge to the classic sensibility, while letting her enjoy the evening without compromising on the impact of the look.",
  },
};

// ------------------------------------------------------------
// DESIGNERS & VENDORS (the scrolling marquee)
// ------------------------------------------------------------
const designers = {
  eyebrow: "The Network",
  heading: "Designers & vendors we've worked with",
  intro: "Years of relationships, opened for you. A partial list of the houses, ateliers and artisans in my little black book.",
  names: [
    "Sabyasachi",
    "Tarun Tahiliani",
    "Anamika Khanna",
    "Raw Mango",
    "Manish Malhotra",
    "Abu Jani Sandeep Khosla",
    "JJ Valaya",
    "Anita Dongre",
    "Ritu Kumar",
    "Falguni Shane Peacock",
    "Kresha Bajaj",
    "House of Masaba",
  ],
};

// ------------------------------------------------------------
// CELEBRITY STYLING (Spotlight cards)
// To change a photo: replace the file in src/assets/spotlight
// keeping the same filename, or update the import at the top.
// ------------------------------------------------------------
const spotlight = {
  eyebrow: "The Spotlight",
  heading: "Celebrity Styling",
  intro: "From fashion week ramps to red carpets - selected moments with names you know.",
  items: [
    {
      id: "spot-01",
      title: "Vaani Kapoor",
      client: "Vaani Kapoor",
      role: "Indian Actress",
      location: "",
      category: "Bombay Times Fashion Week | Rocky Star",
      images: [vaani1, vaani2],
      details: "",
    },
    {
      id: "spot-02",
      title: "Manushi Chhillar",
      client: "Manushi Chhillar",
      role: "Indian Actress, Miss World 2017",
      location: "",
      category: "",
      images: [manushi1, manushi2],
      details: "",
    },
    {
      id: "spot-03",
      title: "Mrunal Thakur",
      client: "Mrunal Thakur",
      role: "Indian Actress",
      location: "",
      category: "",
      images: [mrunal1, mrunal2],
      details: "",
    },
    {
      id: "spot-04",
      title: "Chitrangada Singh",
      client: "Chitrangada Singh",
      role: "Indian Actress",
      location: "",
      category: "",
      images: [chitrangada1, chitrangada2, chitrangada3],
      details: "",
    },
  ],
};

// ------------------------------------------------------------
// TESTIMONIALS
// ------------------------------------------------------------
const testimonials = {
  eyebrow: "Testimonials",
  heading: "What brides & wedding professionals have to say",
  items: [
    {
      quote: "Planning from another city felt impossible until Vrisha took over. Appointments were booked and pieces were shortlisted before I even landed - not a single hour was wasted. My trousseau ended up being the easiest part of my wedding.",
      author: "Juhi Mehta",
      role: "Bride",
      location: "New York",
    },
    {
      quote: "We never imagined we could feel this comfortable while looking this good. Nothing felt forced and nothing felt overdone - we simply felt like ourselves, on our very best day.",
      author: "Rupaah & Kunal Mehta",
      role: "Mother & Father of the Bride",
      location: "Mumbai",
    },
    {
      quote: "Vrisha has a wonderful way of understanding what suits you. She understood what I naturally gravitate towards and brought that into every wedding look, from the sari and jewellery to the smallest details. Everything felt elegant, considered and completely me.",
      author: "Amisha",
      role: "Mother of the Groom",
      location: "Mumbai",
    },
    {
      quote: "She styled our entire side of the family and somehow made each of us feel individually seen. The wedding photographs look like one beautiful, coherent story.",
      author: "Vaishnevi S Jumaani",
      role: "Sister of the Groom",
      location: "Mumbai",
    },
    {
      quote: "Having worked alongside Vrisha, what I appreciate most is the level of thought she brings to every bride she works with. She is detail-oriented, well connected with designers and vendors, and very clear in her communication, which makes her incredibly easy to work with.",
      author: "Vaibhav Kejriwal",
      role: "Arika Events",
      location: "Wedding Planner",
    },
  ],
};

// ------------------------------------------------------------
// ENQUIRE SECTION (contact form text)
// ------------------------------------------------------------
const enquire = {
  eyebrow: "Enquire",
  heading: "Let's begin with a conversation",
  intro: "Tell us a little about your wedding and what you're envisioning. We'd love to hear from you and will get back to you personally.",
  serviceOptions: [
    "Bridal Styling",
    "Personal Shopping",
    "Fine Jewellery",
    "Family & Bridal Party",
    "Consultancy & Concept",
  ],
  successMessage: "Thank you - your enquiry is with us. Expect a personal note within two days.",
  errorMessage: "Something went wrong - please email us directly instead.",
  formNote: "Prefer to write directly? Email us anytime.",
};

// ------------------------------------------------------------
// FOOTER
// ------------------------------------------------------------
const footer = {
  line: "Styled with intention, in India and beyond.",
  copyright: "© Vrisha Muni",
};

const content = {
  brand,
  nav,
  hero,
  about,
  services,
  looks,
  lookbookCaptions,
  designers,
  spotlight,
  testimonials,
  enquire,
  footer,
};

export default content;
